import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Search,
  Clock,
  Mail,
  Phone,
  Sparkles,
} from "lucide-react";
import { API_BASE } from "../lib/api";
import { Input } from "../components/ui/input";
import ContactFormDialog from "../components/ContactFormDialog";
import ChipGroup from "../components/ChipGroup";
import { areaIcon, areaLabel } from "../lib/courseAreas";

const LEVEL_LABEL = {
  base: "Base",
  intermedio: "Intermedio",
  avanzato: "Avanzato",
};

function formatEUR(v) {
  try {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
    }).format(v);
  } catch {
    return `€ ${Number(v).toFixed(2)}`;
  }
}

function CatalogTopbar() {
  return (
    <header
      data-testid="catalog-topbar"
      className="bg-white border-b border-brand-line h-16 flex items-center justify-between px-4 sm:px-6 lg:px-8 sticky top-0 z-40"
    >
      <Link to="/" className="flex items-center gap-2.5">
        <img
          src={`${process.env.PUBLIC_URL}/logo-academy-full.png`}
          alt="Buro Academy"
          className="h-8 w-auto"
        />
      </Link>
      <nav className="flex items-center gap-4">
        <Link
          to="/"
          data-testid="catalog-topbar-home"
          className="hidden sm:inline text-sm font-semibold text-brand-muted hover:text-brand-blue"
        >
          Home
        </Link>
        <Link
          to="/corsi"
          data-testid="catalog-topbar-catalog"
          className="hidden sm:inline text-sm font-semibold text-brand-blue"
        >
          Catalogo Corsi
        </Link>
        <Link
          to="/login"
          data-testid="catalog-topbar-login"
          className="btn-primary text-sm inline-flex items-center gap-2"
        >
          Accedi <ArrowRight size={16} />
        </Link>
      </nav>
    </header>
  );
}

function CatalogCard({ course }) {
  const AreaIcon = areaIcon(course.area);
  return (
    <Link
      to={`/corsi/${course.id}`}
      data-testid={`catalog-card-${course.id}`}
      className="group bg-white border border-brand-line rounded-sm overflow-hidden hover:shadow-md hover:border-brand-blue/40 transition-[box-shadow,border-color] duration-200 flex flex-col"
    >
      <div className="aspect-video bg-brand-blue/5 border-b border-brand-line flex items-center justify-center overflow-hidden relative">
        <AreaIcon size={44} className="text-brand-blue/60" strokeWidth={1.25} />
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className="text-[10px] uppercase tracking-widest bg-brand-blue/5 text-brand-blue border border-brand-blue/15 rounded-sm px-1.5 py-0.5">
            {areaLabel(course.area)}
          </span>
          <span className="text-[10px] uppercase tracking-widest bg-brand-gold/10 text-brand-gold-deep border border-brand-gold/30 rounded-sm px-1.5 py-0.5">
            {LEVEL_LABEL[course.level] || course.level}
          </span>
        </div>
        <h3 className="text-lg font-semibold text-brand-blue mb-2 leading-snug">
          {course.title}
        </h3>
        {course.short_description && (
          <p className="text-sm text-brand-muted leading-relaxed mb-4 line-clamp-3">
            {course.short_description}
          </p>
        )}
        {course.ai_generated_content && (
          <div
            data-testid={`catalog-ai-badge-${course.id}`}
            className="inline-flex items-center gap-1.5 text-[11px] font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded-sm px-2 py-1 mb-3 w-fit"
          >
            <Sparkles size={12} /> Presenti contenuti generati con IA
          </div>
        )}
        <div className="mt-auto pt-4 border-t border-brand-line flex items-end justify-between gap-3">
          <div>
            {course.duration_hours != null && (
              <div className="flex items-center gap-1.5 text-xs text-brand-muted mb-1">
                <Clock size={12} /> {course.duration_hours} ore
              </div>
            )}
            <div className="text-2xl font-bold text-brand-blue">
              {formatEUR(course.price)}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-brand-muted">
              IVA esclusa · a partecipante
            </div>
          </div>
          <span className="text-sm font-semibold text-brand-blue inline-flex items-center gap-1 group-hover:gap-2 transition-[gap]">
            Scopri <ArrowRight size={14} />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function PublicCatalog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [area, setArea] = useState("all");
  const [areaOptions, setAreaOptions] = useState([{ value: "all", label: "Tutte le aree" }]);
  const [search, setSearch] = useState("");
  const [contactOpen, setContactOpen] = useState(false);

  // Aree reali a catalogo → chip di filtro (l'elenco emerge dai corsi pubblicati).
  useEffect(() => {
    axios
      .get(`${API_BASE}/public/catalog/areas`)
      .then((r) => setAreaOptions([
        { value: "all", label: "Tutte le aree" },
        ...(r.data || []).map((a) => ({ value: a, label: a })),
      ]))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (area !== "all") params.area = area;
      if (search.trim()) params.search = search.trim();
      const { data } = await axios.get(`${API_BASE}/public/catalog`, { params });
      setItems(data || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [area, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div data-testid="public-catalog-page" className="min-h-screen bg-white">
      <CatalogTopbar />

      {/* Hero */}
      <section className="brand-grid-bg border-b border-brand-line">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <div className="text-xs font-semibold uppercase tracking-widest text-brand-gold mb-3">
            — Catalogo Corsi
          </div>
          <h1
            data-testid="catalog-title"
            className="text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-brand-ink mb-4"
          >
            I nostri percorsi <span className="text-brand-blue">formativi</span>
          </h1>
          <p className="text-lg text-brand-muted max-w-2xl">
            Corsi specialistici su gare d'appalto, offerte tecniche e sistemi di
            gestione ISO. Scegli il percorso più adatto e scarica il modulo di
            iscrizione con i prezzi dedicati agli addetti.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-b border-brand-line py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
          <div className="relative max-w-xl">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted"
            />
            <Input
              data-testid="catalog-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per titolo o argomento…"
              className="h-11 pl-9 rounded-sm"
            />
          </div>
          {areaOptions.length > 1 && (
            <ChipGroup
              testid="catalog-filter-area"
              ariaLabel="Filtra per area"
              options={areaOptions}
              value={area}
              onChange={setArea}
            />
          )}
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="text-center text-brand-muted py-16">Caricamento catalogo…</div>
        ) : items.length === 0 ? (
          <div
            data-testid="catalog-empty"
            className="bg-white border border-dashed border-brand-line rounded-sm p-14 text-center"
          >
            <div className="text-lg text-brand-ink mb-2">
              Nessun corso corrisponde ai filtri selezionati
            </div>
            <p className="text-sm text-brand-muted mb-4">
              Prova a modificare i filtri o contatta l'amministrazione per
              richiedere un percorso su misura.
            </p>
            <button
              onClick={() => setContactOpen(true)}
              data-testid="catalog-empty-contact-btn"
              className="btn-primary inline-flex items-center gap-2"
            >
              Contatta l'amministrazione
            </button>
          </div>
        ) : (
          <div
            data-testid="catalog-grid"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {items.map((c) => (
              <CatalogCard key={c.id} course={c} />
            ))}
          </div>
        )}
      </section>

      {/* Contact CTA */}
      <section className="bg-brand-blue text-white py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl mb-3 text-white">
            Non trovi il corso che ti serve?
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto mb-6">
            Progettiamo percorsi formativi personalizzati per aziende ed enti pubblici,
            in aula o a distanza. Scrivici per una proposta dedicata.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <button
              onClick={() => setContactOpen(true)}
              data-testid="catalog-contact-btn"
              className="inline-flex items-center gap-2 rounded-sm bg-white text-brand-blue font-semibold px-5 py-2.5 hover:bg-white/90"
            >
              <Mail size={16} /> Contatta l'amministrazione
            </button>
            <a
              href="tel:+39011393770"
              className="inline-flex items-center gap-2 rounded-sm border border-white/40 text-white px-5 py-2.5 hover:bg-white/10"
            >
              <Phone size={16} /> +39 011 393 770
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-brand-line py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <img
              src={`${process.env.PUBLIC_URL}/logo-academy-full.png`}
              alt=""
              className="h-7 w-auto"
            />
          </div>
          <div className="text-sm text-brand-muted">
            © {new Date().getFullYear()} Burocratica Servizi
          </div>
        </div>
      </footer>

      <ContactFormDialog open={contactOpen} onOpenChange={setContactOpen} />
    </div>
  );
}
